import { useCallback } from 'react';

import { OasMemoryError } from 'core/atoms/errors';
import { OasError } from 'core/atoms/errors/oas-error';

import { gql, useMutation } from 'core/dna/types/apollo';
import {
  CreateClass,
  CreateClassVariables,
} from 'core/dna/types/generated/CreateClass';

import { useLazyGetTermsState } from 'core/memory/apollo/terms/local';

import { GET_CLASSES_QUERY } from './_gql';

export interface CreateClassInput {
  name: string;
}

const CREATE_CLASS_MUTATION = gql`
  mutation CreateClass($input: CreateClassInput) {
    createClass(input: $input) {
      id
      name
    }
  }
`;

const TITLE = 'UseCreateClass';

export const useCreateClass = () => {
  const { getTermState } = useLazyGetTermsState();

  const [createClass, { loading, error }] = useMutation<
    CreateClass,
    CreateClassVariables
  >(CREATE_CLASS_MUTATION);

  const func = useCallback(
    async (input: CreateClassInput) => {
      try {
        const termsState = await getTermState(true);

        const result = await createClass({
          variables: {
            input: {
              ...input,
              termId: termsState!.selected!,
            },
          },
          refetchQueries: [
            {
              query: GET_CLASSES_QUERY,
              variables: { termId: termsState!.selected! },
            },
          ],
        });

        if (result.errors) {
          throw new OasMemoryError(JSON.stringify(result.errors), {
            title: TITLE,
          });
        }
        return result.data?.createClass ?? undefined;
      } catch (e: any) {
        if (e instanceof OasError) {
          throw e;
        } else {
          throw OasMemoryError.fromError(e, {
            title: TITLE,
          });
        }
      }
    },
    [createClass, getTermState],
  );

  return {
    createClass: func,
    loading,
    error,
  };
};
